import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { StoreDefaultWorkingTime } from '../../../classes/models/store/StoreDefaultWorkingTime';
import { DayOfWeek } from '../../../classes/enums/DayOfWeek';
import {
  AddStoreDefaultWorkingHoursRequest,
  EditStoreDefaultWorkingHoursRequest
} from '../../../interfaces/request/StoreRequests';

/**
 * This service communicates with backend to handle store default working time data management.
 */
@Injectable({
  providedIn: 'root'
})
export class StoreDefaultWorkingTimeService {
  private http = inject(HttpClient);

  url = environment.apiUrl + '/stores';

  /**
   * Adds new default working times for the given store and start date.
   * @param storeId an identifier of a store.
   * @param defaultWorkingTime default store working times to add.
   */
  addDefaultStoreWorkingTime(storeId: string, defaultWorkingTime: StoreDefaultWorkingTime) {
    const url = `${this.url}/${storeId}/default-working-hours`;
    const monday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.MONDAY);
    const tuesday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.TUESDAY);
    const wednesday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.WEDNESDAY);
    const thursday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.THURSDAY);
    const friday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.FRIDAY);
    const saturday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.SATURDAY);
    const sunday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.SUNDAY);
    const requestBody: AddStoreDefaultWorkingHoursRequest = {
      date: defaultWorkingTime.startDate,
      monday: { openingTime: monday.openingTime, closingTime: monday.closingTime },
      tuesday: { openingTime: tuesday.openingTime, closingTime: tuesday.closingTime },
      wednesday: { openingTime: wednesday.openingTime, closingTime: wednesday.closingTime },
      thursday: { openingTime: thursday.openingTime, closingTime: thursday.closingTime },
      friday: { openingTime: friday.openingTime, closingTime: friday.closingTime },
      saturday: { openingTime: saturday.openingTime, closingTime: saturday.closingTime },
      sunday: { openingTime: sunday.openingTime, closingTime: sunday.closingTime }
    };
    return this.http.post(url, requestBody);
  }

  /**
   * Edits default working times for the given store and start date.
   * @param storeId an identifier of a store.
   * @param defaultWorkingTime default store working times to edit.
   */
  editDefaultStoreWorkingTime(storeId: string, defaultWorkingTime: StoreDefaultWorkingTime) {
    const url = `${this.url}/${storeId}/default-working-hours/${defaultWorkingTime.startDate}`;
    const monday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.MONDAY);
    const tuesday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.TUESDAY);
    const wednesday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.WEDNESDAY);
    const thursday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.THURSDAY);
    const friday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.FRIDAY);
    const saturday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.SATURDAY);
    const sunday = defaultWorkingTime.weeklyTimes.get(DayOfWeek.SUNDAY);
    const requestBody: EditStoreDefaultWorkingHoursRequest = {
      monday: { openingTime: monday.openingTime, closingTime: monday.closingTime },
      tuesday: { openingTime: tuesday.openingTime, closingTime: tuesday.closingTime },
      wednesday: { openingTime: wednesday.openingTime, closingTime: wednesday.closingTime },
      thursday: { openingTime: thursday.openingTime, closingTime: thursday.closingTime },
      friday: { openingTime: friday.openingTime, closingTime: friday.closingTime },
      saturday: { openingTime: saturday.openingTime, closingTime: saturday.closingTime },
      sunday: { openingTime: sunday.openingTime, closingTime: sunday.closingTime }
    };
    return this.http.put(url, requestBody);
  }

  /**
   * Deletes default store working times for the store.
   * @param storeId an identifier of a store.
   * @param startDate the Monday of the week, from which default working hours will take effect.
   */
  deleteDefaultStoreWorkingTime(storeId: string, startDate: string) {
    const url = `${this.url}/${storeId}/default-working-hours/${startDate}`;
    return this.http.delete(url);
  }
}
